import { computed, watchEffect } from 'vue';
import { toValue, tryOnScopeDispose } from '@vueuse/core';
import { DOCS as DOCS_MODULE } from '@/modules/list';
import { getAppModuleService } from '@/modules/registry';
import { MODE as DRAWER_MODE } from '@/components/base-components/components/drawer/constants';
import type { BaseDrawerMode } from '@/components/base-components/components/drawer/types';

export interface UseDocsOptions {
    drawerMode?: BaseDrawerMode;
}

export function useDocs(root?: string, options: UseDocsOptions = {}) {
    const docsRoot = computed(() => toValue(root));
    const drawerMode = computed(() => options.drawerMode ?? DRAWER_MODE.PERSISTENT);

    function getService() {
        return getAppModuleService(DOCS_MODULE);
    }

    watchEffect(() => {
        const service = getService();

        if (!service) return;

        service.ensureLoaded();
    });

    const context = getService()?.registerContext({
        root: docsRoot,
    });

    const service = computed(() => getService());
    const isAvailable = computed(() => service.value?.isAvailable(docsRoot.value) ?? false);
    const isOpen = computed(() => service.value?.isOpen(docsRoot.value) ?? false);

    function open(slug?: string) {
        return getService()?.open({
            root: docsRoot.value,
            slug: slug ?? docsRoot.value,
            mode: drawerMode.value,
        }) ?? false;
    }

    function close() {
        getService()?.closeDrawer();
    }

    function toggle(slug?: string) {
        return getService()?.toggle({
            root: docsRoot.value,
            slug: slug ?? docsRoot.value,
            mode: drawerMode.value,
        }) ?? false;
    }

    tryOnScopeDispose(() => {
        context?.unregister();
        close();
    });

    return {
        isAvailable,
        service,
        isOpen,
        open,
        close,
        toggle,
    };
}

export default useDocs;
